import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { authAPI } from '../services/api';

const WHATSAPP_NUMBER  = '919347743724';   // ASR INFOTECH sales
const CONTACT_PHONE    = '9347743724';
const CONTACT_EMAIL    = 'anilreddy@asrinfotech.co.in';
const CONTACT_WEBSITE  = 'www.asrinfotech.co.in';
const REMEMBER_KEY     = 'asr_login_remember';

export default function Login({ onLogin, onRegister }) {
  const [schoolCode, setSchoolCode] = useState('');
  const [username,   setUsername]   = useState('');
  const [password,   setPassword]   = useState('');
  const [showPw,     setShowPw]     = useState(false);
  const [remember,   setRemember]   = useState(true);
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const [slowLogin,  setSlowLogin]   = useState(false);

  // Load "Remember me" values + accept ?school= URL param
  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.schoolCode) setSchoolCode(saved.schoolCode);
        if (saved.username)   setUsername(saved.username);
      }
    } catch { /* ignore parse errors */ }
    const params = new URLSearchParams(window.location.search);
    const s = params.get('school');
    if (s) setSchoolCode(s.toLowerCase().trim());
  }, []);

  useEffect(() => {
    let t;
    if (loading) t = setTimeout(() => setSlowLogin(true), 5000);
    else setSlowLogin(false);
    return () => clearTimeout(t);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.login(schoolCode.trim().toLowerCase(), username, password);

      // Persist / clear remember-me values (password never stored)
      try {
        if (remember) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({
            schoolCode: schoolCode.trim().toLowerCase(),
            username,
          }));
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
      } catch { /* localStorage may be blocked */ }

      onLogin(
        res.data.token,
        schoolCode.trim().toLowerCase(),
        res.data.school_name,
        res.data.must_change_password,
        res.data.allowed_tabs,
        res.data.school_location,
        res.data.school_tagline,
        res.data.contact_name,
        res.data.whatsapp_enabled,
        res.data.certificates_enabled,
        res.data.biometric_enabled,
        res.data.full_name || '',
        res.data.employee_biometric_enabled,
        res.data.student_biometric_enabled,
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid school code, username or password.');
    } finally {
      setLoading(false);
    }
  };

  const openWhatsAppDemo = () => {
    const msg = encodeURIComponent(
      "Hi ASR INFOTECH, I'd like to book a free demo of the School ERP system.",
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="lp-root">
      {/* ── Left panel ── */}
      <div className="lp-left">
        {/* Line-art school building background */}
        <svg className="lp-bg-illust" viewBox="0 0 900 600" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
          <g stroke="rgba(255,255,255,0.08)" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Clouds */}
            <path d="M120 110 q10 -18 30 -18 q6 -14 22 -14 q16 0 22 14 q18 0 22 16 q0 12 -14 14 h-70 q-16 -2 -12 -12z"/>
            <path d="M700 90 q8 -14 24 -14 q6 -12 20 -12 q14 0 20 12 q16 0 20 14 q0 10 -12 12 h-60 q-14 -2 -12 -12z"/>
            {/* Flag pole */}
            <line x1="450" y1="90" x2="450" y2="180"/>
            <path d="M450 100 l40 8 l-40 12z"/>
            {/* Roof / pediment */}
            <polygon points="230,200 450,120 670,200"/>
            <circle cx="450" cy="180" r="14"/>
            <line x1="450" y1="170" x2="450" y2="180"/>
            <line x1="450" y1="180" x2="458" y2="184"/>
            {/* Main building */}
            <rect x="200" y="200" width="500" height="240" rx="4"/>
            {/* Columns */}
            <line x1="240" y1="220" x2="240" y2="440"/>
            <line x1="310" y1="220" x2="310" y2="440"/>
            <line x1="380" y1="220" x2="380" y2="440"/>
            <line x1="450" y1="220" x2="450" y2="440"/>
            <line x1="520" y1="220" x2="520" y2="440"/>
            <line x1="590" y1="220" x2="590" y2="440"/>
            <line x1="660" y1="220" x2="660" y2="440"/>
            {/* Base steps */}
            <rect x="180" y="440" width="540" height="14"/>
            <rect x="160" y="454" width="580" height="14"/>
            {/* Door */}
            <rect x="425" y="360" width="50" height="80" rx="24"/>
            {/* Trees */}
            <ellipse cx="90" cy="440" rx="50" ry="24"/>
            <line x1="90" y1="464" x2="90" y2="500"/>
            <ellipse cx="820" cy="450" rx="46" ry="22"/>
            <line x1="820" y1="472" x2="820" y2="500"/>
            {/* Ground line */}
            <line x1="0" y1="500" x2="900" y2="500"/>
          </g>
        </svg>

        <motion.div
          className="lp-left-content"
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >
          <div className="lp-brand">
            <div className="lp-brand-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M20 3L36 10V20C36 28 29 35 20 37C11 35 4 28 4 20V10L20 3Z" fill="url(#lpBrandGrad)"/>
                <path d="M12 18L20 14L28 18L20 22L12 18Z" fill="#fff"/>
                <path d="M20 22V27M14 20V24C14 24 16 26 20 26C24 26 26 24 26 24V20" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                <defs>
                  <linearGradient id="lpBrandGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#8b5cf6"/>
                    <stop offset="100%" stopColor="#6366f1"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="lp-brand-text">
              <span className="lp-brand-name">ASR INFOTECH</span>
              <span className="lp-brand-tag">Smart Solutions, Better Education</span>
            </div>
          </div>

          <div className="lp-hero">
            <h1 className="lp-hero-title">
              Complete<br/>School <span className="lp-hero-accent">ERP</span>
            </h1>
            <p className="lp-hero-sub">
              All-in-one platform to manage your school operations efficiently
            </p>
            <p className="lp-hero-tags">
              <span>Admissions</span><b>·</b><span>Fees</span><b>·</b><span>Attendance</span><b>·</b><span>Exams</span><b>·</b><span>Transport</span>
              <br/>
              <span>Parent App</span><b>·</b><span>Staff</span><b>·</b><span>Accounts</span><b>·</b><span>Reports &amp; More</span>
            </p>
          </div>

          {/* ── Hero illustration: laptop dashboard + phone parent app ── */}
          <div className="lp-illust" aria-hidden="true">
            {/* Phone (parent app) */}
            <div className="lp-illust-phone">
              <div className="lp-illust-phone-notch"/>
              <div className="lp-illust-phone-screen">
                <div className="lp-illust-phone-head">
                  <span className="lp-illust-back">‹</span>
                  <span>Parent App</span>
                </div>
                <div className="lp-illust-phone-greet">
                  <strong>Hello, Parent!</strong>
                  <small>Here's what's happening today.</small>
                </div>
                <div className="lp-illust-phone-grid">
                  <div className="lp-illust-mini lp-im-1"><b>Attendance</b><i>Present</i></div>
                  <div className="lp-illust-mini lp-im-2"><b>Homework</b><i>2 Pending</i></div>
                  <div className="lp-illust-mini lp-im-3"><b>Fees Due</b><i>₹2,500</i></div>
                  <div className="lp-illust-mini lp-im-4"><b>Exam</b><i>Unit Test</i></div>
                  <div className="lp-illust-mini lp-im-5"><b>Transport</b><i>On Time</i></div>
                  <div className="lp-illust-mini lp-im-6"><b>Notices</b><i>3 New</i></div>
                </div>
              </div>
            </div>

            {/* Laptop (admin dashboard) */}
            <div className="lp-illust-laptop">
              <div className="lp-illust-laptop-screen">
                <div className="lp-illust-side">
                  <div className="lp-illust-side-brand">
                    <span className="lp-illust-side-logo"/>
                    <span>ASR</span>
                  </div>
                  <div className="lp-illust-menu lp-illust-menu-active">Dashboard</div>
                  <div className="lp-illust-menu">Students</div>
                  <div className="lp-illust-menu">Employees</div>
                  <div className="lp-illust-menu">Fees</div>
                  <div className="lp-illust-menu">Reports</div>
                </div>
                <div className="lp-illust-main">
                  <div className="lp-illust-kpis">
                    <div className="lp-illust-kpi">
                      <span>Total Students</span>
                      <strong>1,248</strong>
                      <em className="up">+12% this month</em>
                    </div>
                    <div className="lp-illust-kpi">
                      <span>Total Fees Collected</span>
                      <strong>₹12,45,680</strong>
                      <em className="up">+8% this month</em>
                    </div>
                    <div className="lp-illust-kpi">
                      <span>Attendance Today</span>
                      <strong>92.6%</strong>
                      <em className="up">+3% this month</em>
                    </div>
                    <div className="lp-illust-kpi">
                      <span>Total Staff</span>
                      <strong>78</strong>
                      <em className="up">+3 this month</em>
                    </div>
                  </div>
                  <div className="lp-illust-row2">
                    <div className="lp-illust-chart">
                      <div className="lp-illust-chart-head">
                        <span>Fee Collection</span><small>This Month</small>
                      </div>
                      <svg viewBox="0 0 220 80" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="lpChartFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.5"/>
                            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0"/>
                          </linearGradient>
                          <linearGradient id="lpChartLine" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#a78bfa"/>
                            <stop offset="100%" stopColor="#8b5cf6"/>
                          </linearGradient>
                        </defs>
                        <path d="M0,60 C30,58 45,52 60,48 C80,42 95,38 115,32 C140,26 165,22 190,14 L220,10 L220,80 L0,80 Z" fill="url(#lpChartFill)"/>
                        <path d="M0,60 C30,58 45,52 60,48 C80,42 95,38 115,32 C140,26 165,22 190,14 L220,10" fill="none" stroke="url(#lpChartLine)" strokeWidth="2"/>
                        <circle cx="140" cy="24" r="3" fill="#a78bfa"/>
                      </svg>
                    </div>
                    <div className="lp-illust-acts">
                      <div className="lp-illust-acts-head">Recent Activities</div>
                      <div className="lp-illust-act"><b>New Admission</b><i>Aditya Kumar in Grade 5</i></div>
                      <div className="lp-illust-act"><b>Fee Payment</b><i>Received from Rahul Sharma</i></div>
                      <div className="lp-illust-act"><b>Attendance Updated</b><i>Grade 8 — 92%</i></div>
                      <div className="lp-illust-act"><b>Exam Scheduled</b><i>Unit Test — Grade 10</i></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lp-illust-laptop-base"/>
            </div>
          </div>

          {/* ── Feature cards (light, colored icons) ── */}
          <div className="lp-feature-grid">
            <div className="lp-feat-card">
              <span className="lp-feat-card-icon lp-fc-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              </span>
              <div>
                <strong>Admissions</strong>
                <span>Online admissions made easy</span>
              </div>
            </div>
            <div className="lp-feat-card">
              <span className="lp-feat-card-icon lp-fc-2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12M6 3l6 8 6-8M6 3v18M18 3v18M6 21h12M9 13a3 3 0 006 0"/></svg>
              </span>
              <div>
                <strong>Fee Collection</strong>
                <span>Online payments &amp; receipts</span>
              </div>
            </div>
            <div className="lp-feat-card">
              <span className="lp-feat-card-icon lp-fc-3">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/></svg>
              </span>
              <div>
                <strong>Attendance</strong>
                <span>Real-time attendance tracking</span>
              </div>
            </div>
            <div className="lp-feat-card">
              <span className="lp-feat-card-icon lp-fc-4">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>
              </span>
              <div>
                <strong>Examinations</strong>
                <span>Manage exams &amp; results</span>
              </div>
            </div>
            <div className="lp-feat-card">
              <span className="lp-feat-card-icon lp-fc-5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="3" ry="3"/><line x1="12" y1="18.5" x2="12" y2="18.5"/></svg>
              </span>
              <div>
                <strong>Parent App</strong>
                <span>Stay connected with parents</span>
              </div>
            </div>
            <div className="lp-feat-card">
              <span className="lp-feat-card-icon lp-fc-6">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 8h16v9H2z"/><path d="M18 12h3l1 3v2h-4"/><circle cx="6" cy="18" r="2"/><circle cx="16" cy="18" r="2"/></svg>
              </span>
              <div>
                <strong>Transport</strong>
                <span>School bus tracking in real-time</span>
              </div>
            </div>
          </div>

          {/* ── Stats strip (single horizontal row) ── */}
          <div className="lp-stats-row">
            <div className="lp-stat-chip">
              <span className="lp-stat-icon lp-si-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M5 21V9l7-4 7 4v12M9 21v-6h6v6M9 12h.01M15 12h.01"/></svg>
              </span>
              <div>
                <span className="lp-sc-val">Trusted by</span>
                <span className="lp-sc-lbl">50+ Schools</span>
              </div>
            </div>
            <div className="lp-stat-chip">
              <span className="lp-stat-icon lp-si-2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </span>
              <div>
                <span className="lp-sc-val">10,000+</span>
                <span className="lp-sc-lbl">Students Managed</span>
              </div>
            </div>
            <div className="lp-stat-chip">
              <span className="lp-stat-icon lp-si-3">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </span>
              <div>
                <span className="lp-sc-val">99.9%</span>
                <span className="lp-sc-lbl">Uptime</span>
              </div>
            </div>
            <div className="lp-stat-chip">
              <span className="lp-stat-icon lp-si-4">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
              </span>
              <div>
                <span className="lp-sc-val">100%</span>
                <span className="lp-sc-lbl">Secure &amp; Safe</span>
              </div>
            </div>
          </div>

          <button type="button" className="lp-demo-banner" onClick={openWhatsAppDemo}>
            <span className="lp-demo-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1v-6h3zM3 19a2 2 0 0 0 2 2h1v-6H3z"/></svg>
            </span>
            <div className="lp-demo-text">
              <strong>Need a Demo or Help?</strong>
              <span>We are here to help you!</span>
            </div>
            <span className="lp-demo-cta">
              BOOK FREE DEMO
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </span>
          </button>
        </motion.div>

        {/* Decorative blobs */}
        <div className="lp-blob lp-blob-1" aria-hidden="true"/>
        <div className="lp-blob lp-blob-2" aria-hidden="true"/>
        <div className="lp-blob lp-blob-3" aria-hidden="true"/>
      </div>

      {/* ── Right panel ── */}
      <div className="lp-right">
        <div className="lp-right-bg" aria-hidden="true"/>
        <div className="lp-right-glow" aria-hidden="true"/>
        <div className="lp-right-glow2" aria-hidden="true"/>
        <motion.div
          className="lp-form-wrap"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1], delay: 0.1 }}
        >
          <div className="lp-form-header">
            <div className="lp-form-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
            </div>
            <h2 className="lp-form-title">
              Welcome to <span className="lp-brand-inline">ASR INFOTECH</span>
            </h2>
            <p className="lp-form-sub">School ERP Management System</p>
            <div className="lp-form-underline" aria-hidden="true"/>
          </div>

          <form className="lp-form" onSubmit={handleSubmit} autoComplete="off">
            <div className="lp-field">
              <label htmlFor="school_code">SCHOOL CODE</label>
              <div className="lp-input-wrap">
                <span className="lp-input-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/></svg>
                </span>
                <input
                  id="school_code"
                  type="text"
                  value={schoolCode}
                  onChange={(e) => setSchoolCode(e.target.value.toLowerCase())}
                  placeholder="e.g. geetham"
                  required
                  autoFocus
                />
              </div>
              <small>Your school's short code (not the full name)</small>
            </div>

            <div className="lp-field">
              <label htmlFor="username">USERNAME</label>
              <div className="lp-input-wrap">
                <span className="lp-input-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/></svg>
                </span>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                />
              </div>
            </div>

            <div className="lp-field">
              <label htmlFor="password">PASSWORD</label>
              <div className="lp-input-wrap">
                <span className="lp-input-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
                </span>
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="lp-pw-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  title={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? (
                    <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.450l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"/><path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"/></svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                  )}
                </button>
              </div>
            </div>

            <div className="lp-form-row">
              <label className="lp-remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span className="lp-remember-box" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 10 9 14 15 6"/></svg>
                </span>
                <span>Remember me</span>
              </label>
            </div>

            {error && (
              <div className="lp-error" role="alert">
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                {error}
              </div>
            )}

            {slowLogin && (
              <div className="lp-slow-msg">
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>
                Server is waking up, please wait a moment…
              </div>
            )}

            <button className="lp-submit" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="lp-spinner" aria-hidden="true"/>
                  Signing in…
                </>
              ) : (
                <>
                  SIGN IN
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                </>
              )}
            </button>
          </form>

          <div className="lp-divider"><span>OR</span></div>

          <button type="button" className="lp-whatsapp" onClick={openWhatsAppDemo}>
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06.02C5.5.02.15 5.37.15 11.93c0 2.1.55 4.16 1.6 5.98L0 24l6.28-1.65a11.9 11.9 0 0 0 5.78 1.48h.01c6.55 0 11.9-5.35 11.9-11.91 0-3.18-1.24-6.17-3.45-8.44zM12.07 21.8h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.72.98.99-3.63-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.46 4.44-9.9 9.9-9.9 2.64 0 5.13 1.03 6.99 2.9a9.83 9.83 0 0 1 2.9 6.99c0 5.46-4.44 9.88-9.9 9.88zm5.42-7.4c-.3-.15-1.76-.86-2.03-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z"/>
            </svg>
            Book Free Demo on WhatsApp
          </button>

          {onRegister && (
            <p className="lp-register-row">
              Don't have an account?{' '}
              <button className="lp-register-btn" type="button" onClick={onRegister}>
                Register your school
              </button>
            </p>
          )}

          <div className="lp-ssl">
            <div className="lp-ssl-info">
              <span className="lp-ssl-title">
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
                SSL Secured Connection
              </span>
              <span className="lp-ssl-sub">
                Your data is encrypted and protected with enterprise-grade security.
              </span>
            </div>
            <span className="lp-ssl-badge">100%<br/><small>SECURE</small></span>
          </div>

          <div className="lp-contact">
            <a href={`tel:${CONTACT_PHONE}`} className="lp-contact-item lp-ci-phone">
              <span className="lp-ci-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </span>
              <span className="lp-ci-text">{CONTACT_PHONE}</span>
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="lp-contact-item lp-ci-mail">
              <span className="lp-ci-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </span>
              <span className="lp-ci-text">{CONTACT_EMAIL}</span>
            </a>
            <a href={`https://${CONTACT_WEBSITE}`} target="_blank" rel="noopener noreferrer" className="lp-contact-item lp-ci-web">
              <span className="lp-ci-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              </span>
              <span className="lp-ci-text">{CONTACT_WEBSITE}</span>
            </a>
          </div>

          <p className="lp-copyright">
            <span>© {new Date().getFullYear()} ASR INFOTECH. All rights reserved.</span>
            <span className="lp-copyright-sep">|</span>
            <span>School ERP</span>
            <span className="lp-copyright-sep">|</span>
            <span>Version 1.0</span>
            <span className="lp-copyright-sep">|</span>
            <span>Made in India <span role="img" aria-label="India">🇮🇳</span></span>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
